import React, { useState, useEffect } from 'react';
import { Target, Edit2, Check, X } from 'lucide-react';
import profileExtendedService from '../../services/profileExtended.service';
import portfolioService from '../../services/portfolio.service';
import toast from 'react-hot-toast';
import Button from '@components/common/Button';

const CareerFocusSection = ({ currentIndustryId, onUpdate }) => {
  const [careerDomains, setCareerDomains] = useState([]);
  const [selectedIndustryId, setSelectedIndustryId] = useState(currentIndustryId || '');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCareerDomains();
  }, []);

  useEffect(() => {
    setSelectedIndustryId(currentIndustryId || '');
  }, [currentIndustryId]);

  const fetchCareerDomains = async () => {
    try {
      const response = await portfolioService.getCareerDomains();
      // Handle both response.data.data and response.data structures
      const domainsData = response.data?.data || response.data || [];
      setCareerDomains(Array.isArray(domainsData) ? domainsData : []);
    } catch (error) {
      console.error('Failed to fetch career domains:', error);
      setCareerDomains([]);
    }
  };

  const handleSave = async () => {
    if (!selectedIndustryId) {
      toast.error('Please select an industry');
      return;
    }

    setLoading(true);
    try {
      await profileExtendedService.updateCareerFocus(parseInt(selectedIndustryId));
      toast.success('Career focus updated successfully');
      setIsEditing(false);
      // Trigger parent refresh to update the displayed value
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update career focus');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedIndustryId(currentIndustryId || '');
    setIsEditing(false);
  };

  const getCurrentIndustryName = () => {
    const domain = careerDomains.find(dom => dom.id === parseInt(currentIndustryId));
    return domain?.name || 'Not set';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-base font-semibold flex items-center gap-2">
          <Target className="w-4 h-4 text-navbar" />
          Industry Focus 
        </h3>
        {!isEditing && (
          <Button
            onClick={() => setIsEditing(true)}
            variant="primary"
          >
            <Edit2 className="w-4 h-4 mr-2" />
            Edit
          </Button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Select Industry Type
            </label>
            <select
              value={selectedIndustryId}
              onChange={(e) => setSelectedIndustryId(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select an industry</option>
              {careerDomains.map((domain) => (
                <option key={domain.id} value={domain.id}>
                  {domain.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              disabled={loading}
              variant="primary"
              loading={loading}
            >
              <Check className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Save'}
            </Button>
            <Button
              onClick={handleCancel}
              variant="outline"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="p-3 bg-secondary-50 rounded-lg">
          <p className="text-xs text-gray-600 mb-1">Industry Type</p>
          <p className="text-sm font-semibold text-gray-900">{getCurrentIndustryName()}</p>
        </div>
      )}
    </div>
  );
};

export default CareerFocusSection;
